"use client";
import { Capacitor } from "@capacitor/core";
import {
  deploymentEnvironment,
  isClientTelemetryDeliveryEnabled,
} from "@/lib/telemetry/environment";
import { readClientAcquisition } from "./client";
import { advertisingAllowed } from "./shared";

type Pixel = ((...args: unknown[]) => void) & {
  queue: unknown[][];
  callMethod?: (...args: unknown[]) => void;
  push?: Pixel;
  loaded?: boolean;
  version?: string;
};
type PixelWindow = Window & {
  fbq?: Pixel;
  _fbq?: Pixel;
};
let initialized = false;
/** `init` sent. Meta allows ONE init per pixel; it cannot be updated later. */
let pixelStarted = false;
/** Whether we know who is signed in yet (or gave up waiting). */
let identityKnown = false;
/** Browser events raised before `init`, replayed with their own event IDs. */
let heldTracks: [WebAdEvent, WebAdData, string][] = [];
const IDENTITY_WAIT_MS = 3000;
let previousPath = "";
/** Hashed advanced-matching fields for the signed-in person, if any. */
let pixelUser: Record<string, string> = {};
const META_COOKIE_MAX_AGE = 90 * 24 * 60 * 60;
const META_COOKIE = /^fb\.\d\.\d{10,16}\.[\w-]{1,500}$/;
function readCookie(name: string) {
  const prefix = `${name}=`;
  for (const part of document.cookie.split(";")) {
    const value = part.trim();
    if (value.startsWith(prefix))
      return decodeURIComponent(value.slice(prefix.length));
  }
}
function writeCookie(name: string, value: string) {
  // Same scope the Pixel uses (the registrable domain), so it adopts ours
  // instead of minting a second, different ID.
  const domain = window.location.hostname.endsWith("hiive.club")
    ? "; domain=.hiive.club"
    : "";
  // biome-ignore lint/suspicious/noDocumentCookie: the Cookie Store API is missing from older Safari, which much of the ad traffic uses.
  document.cookie = `${name}=${value}; max-age=${META_COOKIE_MAX_AGE}; path=/; SameSite=Lax${domain}${window.location.protocol === "https:" ? "; Secure" : ""}`;
}
/**
 * Meta's first-party browser (_fbp) and click (_fbc) IDs, created before the
 * Pixel script has loaded. Without this the first events of a visit — the ad
 * landing, the one that matters most — reached the server copy with neither,
 * because fbevents.js sets them asynchronously. Same format the Pixel writes.
 */
function ensureMetaCookies(): { fbp?: string; fbc?: string } {
  let fbp = readCookie("_fbp");
  if (!fbp || !META_COOKIE.test(fbp)) {
    const random = crypto.getRandomValues(new Uint32Array(1))[0] ?? 0;
    fbp = `fb.1.${Date.now()}.${random}`;
    writeCookie("_fbp", fbp);
  }
  let fbc = readCookie("_fbc");
  const fbclid = new URL(window.location.href).searchParams.get("fbclid");
  if (fbclid && /^[\w-]{1,500}$/.test(fbclid) && !fbc?.endsWith(`.${fbclid}`)) {
    fbc = `fb.1.${Date.now()}.${fbclid}`;
    writeCookie("_fbc", fbc);
  }
  return { fbp, fbc: fbc && META_COOKIE.test(fbc) ? fbc : undefined };
}
function script(src: string) {
  const element = document.createElement("script");
  element.async = true;
  element.src = src;
  document.head.appendChild(element);
}
function webAdvertisingActive() {
  return (
    !Capacitor.isNativePlatform() &&
    deploymentEnvironment === "production" &&
    isClientTelemetryDeliveryEnabled()
  );
}
/** Loads and grants the Pixel once consent allows it; false when it may not run. */
function ensurePixel(win: PixelWindow): boolean {
  if (!advertisingAllowed(readClientAcquisition())) {
    win.fbq?.("consent", "revoke");
    return false;
  }
  const meta = process.env.NEXT_PUBLIC_META_PIXEL_ID;
  if (!meta) return false;
  ensureMetaCookies();
  if (!initialized) {
    initialized = true;
    if (!win.fbq) {
      const fn: Pixel = Object.assign(
        (...args: unknown[]) => {
          fn.callMethod ? fn.callMethod(...args) : fn.queue.push(args);
        },
        { queue: [] as unknown[][], loaded: true, version: "2.0" },
      );
      fn.push = fn;
      win.fbq = fn;
      win._fbq = fn;
      script("https://connect.facebook.net/en_US/fbevents.js");
    }
    win.fbq("set", "autoConfig", false, meta);
    // Advanced matching only counts when passed to the one-and-only `init`
    // (a second init is rejected), so init waits briefly for the identity.
    window.setTimeout(() => {
      identityKnown = true;
      startPixel(win);
    }, IDENTITY_WAIT_MS);
  }
  win.fbq?.("consent", "grant");
  startPixel(win);
  return true;
}
function startPixel(win: PixelWindow) {
  const meta = process.env.NEXT_PUBLIC_META_PIXEL_ID;
  if (pixelStarted || !identityKnown || !initialized || !meta || !win.fbq)
    return;
  pixelStarted = true;
  win.fbq("init", meta, pixelUser);
  for (const [event, data, eventId] of heldTracks)
    win.fbq("track", event, data, { eventID: eventId });
  heldTracks = [];
}
export type WebAdEvent =
  | "PageView"
  | "ViewContent"
  | "InitiateCheckout"
  | "AddPaymentInfo";
export type WebAdData = {
  content_ids?: string[];
  content_type?: string;
  value?: number;
  currency?: string;
};
/**
 * Redundant browser + server delivery, deduplicated by one event_id — what
 * Meta's Conversions API Gateway does, without hosting one. The Pixel loses
 * events to ad blockers, ITP and closed tabs; the server copy (with IP, user
 * agent, fbp/fbc and, when signed in, hashed contacts) does not.
 */
function trackRedundant(win: PixelWindow, event: WebAdEvent, data: WebAdData) {
  const eventId = crypto.randomUUID();
  const { fbp, fbc } = ensureMetaCookies();
  if (pixelStarted) win.fbq?.("track", event, data, { eventID: eventId });
  else heldTracks.push([event, data, eventId]);
  try {
    void fetch("/api/acquisition/events", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        event_name: event,
        event_id: eventId,
        url: window.location.href,
        custom_data: data,
        fbp,
        fbc,
      }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* The Pixel copy still stands. */
  }
}
/**
 * One PageView per page. The path is read here, never passed in: two callers
 * once formatted it differently ("/x?" vs "/x"), firing every PageView twice.
 */
export function updateWebAdvertising() {
  if (!webAdvertisingActive()) return;
  const win = window as PixelWindow;
  if (!ensurePixel(win)) return;
  const path = `${window.location.pathname}${window.location.search}`;
  if (previousPath === path) return;
  previousPath = path;
  trackRedundant(win, "PageView", {});
}
/**
 * Browse and checkout intent for Meta's optimisation. Purchase and
 * CompleteRegistration are NOT sent from here: they go server-side from
 * authoritative records, so the browser never double-reports them.
 */
export function trackWebAdEvent(
  event: Exclude<WebAdEvent, "PageView">,
  data: WebAdData,
) {
  if (!webAdvertisingActive()) return;
  const win = window as PixelWindow;
  if (!ensurePixel(win)) return;
  trackRedundant(win, event, data);
}
let userRequest = 0;
/**
 * Advanced matching for the Pixel: the signed-in person's hashed contacts and
 * account ID, so browser events match as well as their server copies. The
 * hashes come from the server (verified contacts only); nothing is hashed or
 * read from the page here. Called with null for a signed-out visitor.
 */
export async function setWebAdvertisingUser(userId: string | null) {
  if (!webAdvertisingActive()) return;
  const request = ++userRequest;
  let next: Record<string, string> = {};
  if (userId) {
    try {
      const response = await fetch("/api/acquisition/matching", {
        cache: "no-store",
      });
      if (response.ok) {
        const body = await response.json();
        for (const key of ["em", "ph", "fn", "ln", "country", "external_id"])
          if (
            typeof body.matching?.[key] === "string" &&
            /^[a-f0-9]{64}$/.test(body.matching[key])
          )
            next[key] = body.matching[key];
      }
    } catch {
      next = {};
    }
  }
  if (request !== userRequest) return;
  // Only the first identity of a page load reaches the Pixel: a sign-in later
  // in the same visit is matched by the server copies, which carry it anyway.
  if (pixelStarted) return;
  pixelUser = next;
  identityKnown = true;
  startPixel(window as PixelWindow);
}
